import Btn from 'components/buttons/Btn'
import { BtnDesc } from 'components/form'
import i18next from 'i18next'
import { sumBy } from 'lodash-es'
import { Trans } from 'react-i18next'
import { Link } from 'react-router-dom'
import { store } from 'state/_store'
import { StarPrinter } from 'state/models/StarPrinter'
import { getEnabledPrinters } from 'state/Printers'
import Alerts from './Alerts'
import { saveFile } from './index'
import platform from './platform'

export default class StarPrinters {

	//TODO: is this global enough? should we place this in window, or localstorage instead?
	static #printQueue = []
	static #isPrinting = false

	/**
	 * Should Star Printing be available in the current platform? Either an updated app, or a dev environment (which would
	 * use {@link _setupMock()} at {@link isInterfaceEnabled()}.
	 * Mostly for UI usage.
	 * @returns {boolean}
	 */
	static shouldBeAvailable() {
		return platform.isNative() || platform.isDev()
	}

	static isInterfaceEnabled(silent = false) {
		if (typeof window.starprnt == 'undefined') {
			if (platform.isDev()) {
				StarPrinters._setupMock()
			} else {
				if (silent) {
					console.warn('Star printers are not supported on this platform.')
				} else {
					Alerts.danger(i18next.t('Star printers are not supported on this platform.')+' '+i18next.t('CONTACT_ERROR'))
				}
				return false
			}
		}
		return true
	}

	/**
	 * Just to test printer stuff in Web without actual support
	 * @private
	 */
	static _setupMock() {
		const SUCCESS_CHANCE = 66.7
		console.warn('Creating dev mock of StarPrinter plugin!')

		//FIXME: move all those cordova mocks to a single place
		/**
		 * @var window.starprnt {object} If we're in Cordova, this would be an object (from cordova-plugin-starprnt)
		 * @see https://github.com/auctifera-josed/starprnt?tab=readme-ov-file#api-reference
		 */
		window.starprnt = {
			portDiscovery: (connection, success, error) => {
				window.setTimeout(() => {
					const chance = Math.random() * 100
					console.log(`StarPrinters mock looking for ${connection} printers; chance got: ${chance} / ${SUCCESS_CHANCE}%`)
					if (chance < SUCCESS_CHANCE) {
						console.log('StarPrinters mock "found" two printers')
						success([
							{ portName: 'TCP:192.168.1.7', macAddress: 'AA:BB:CC:DD', modelName: 'TestPrinter I' },
							{ portName: 'USB:SN:2000601000000405', macAddress: 'AA:BB:CC:EE', modelName: 'Test TSP143IIIU' },
						])
					} else if (chance < SUCCESS_CHANCE + ((100 - SUCCESS_CHANCE) / 2)) {
						console.log('StarPrinters mock "found" no printer')
						success([])
					} else {
						error(new Error('Mock error produced.'))
					}
				}, 500)
			},

			printImage: (port, emulator, image, success, error) => {
				const chance = Math.random() * 100
				const finish = image.uri.startsWith('/silence')? //dirty hack to silence mock auto-prints
					success :
					() => {
						(chance <= SUCCESS_CHANCE)?
							success() :
							error(i18next.t('fake test error'))
					}
				window.setTimeout(finish, chance * 40)
			}
		}
	}

	/**
	 *
	 * @param setPrinters
	 * @param showAlerts
	 * @param callback {function} Called on both success and failure, no args
	 */
	static search(setPrinters, showAlerts, callback = () => {}) {
		console.log('Searching for printers')

		let wrapUp = false
		const successCallback = printers => {
			if (wrapUp) {
				console.warn('Wrap-up already called, skipping success callback')
				return
			}
			wrapUp = true
			printers = StarPrinter.map(printers).map(p => Object.assign(p)) //fields normalization
			store.dispatch(setPrinters(printers))
			if (showAlerts && printers.length == 0) {
				Alerts.warning(<Trans>No printers were found. Try again with the <BtnDesc icon="print-search"/>.</Trans>)
			}
			callback()
		}

		const errorCallback = error => {
			if (wrapUp) {
				console.warn('Wrap-up already called, skipping error callback')
				return
			}
			wrapUp = true
			console.error(error)
			if (showAlerts) {
				Alerts.danger(i18next.t('An error occurred while searching for printers.')+' '+i18next.t('CONTACT_TRY_AGAIN')+' ('+error+')')
			}
			callback()
		}

		//give up on the search after a given timeout, so the user can move around or try to restart the search
		window.setTimeout(() => {
			console.warn('Timeout reached during printer search :(')
			successCallback([])
		}, 20_000) //TODO replace with MS.MIN()

		window.starprnt.portDiscovery('All', successCallback, errorCallback)
	}

	static NoPrinterWarning() {
		return <Trans>There are no printers selected. You can change that in the <Btn as={Link} to="/settings" variant="link" icon="cog" swap>Settings</Btn></Trans>
	}

	/**
	 * Public interface for requesting to print an order receipt.
	 * Actually adds the order in a queue for further processing.
	 * @see queueIsEmpty
	 * @param order {Order}
	 * @param silent {boolean}
	 */
	static print(order, silent = false) {
		if (!StarPrinters.isInterfaceEnabled(silent)) {
			return false
		}

		if (!getEnabledPrinters(store.getState()).length) {
			if (silent) {
				console.warn('No printers selected while silent printing was called.')
			} else {
				// noinspection JSIgnoredPromiseFromCall
				Alerts.warning(<Trans>There are no printers selected. You can change that in the <Btn as={Link} to="/settings" variant="link" icon="cog" swap>Settings</Btn></Trans>, { perm: true })
			}
			return false
		}

		StarPrinters.#queue(order, silent)
	}

	static get queueIsEmpty() {
		return !StarPrinters.#printQueue.length
	}

	/**
	 * @param order {Order}
	 * @param silent {boolean}
	 */
	static #queue(order, silent) {
		if (StarPrinters.#isPrinting) {
			(silent? console.debug : Alerts.info)(i18next.t('Order #{{orderNumber}} has been queued for printing.', { orderNumber: order.number }))
		}

		//queues the order then makes sure it prints sometime (in case processing is done right before we push the order?)
		StarPrinters.#printQueue.push([order, silent])
		StarPrinters.#processQueue()
	}

	/**
	 * Prints the next order in the queue, if any.
	 */
	static async #processQueue() {
		if (StarPrinters.#isPrinting) {
			return //so let's just wait
		}

		StarPrinters.#isPrinting = true

		if (StarPrinters.queueIsEmpty) {
			console.debug('All orders already got printed...?') //TODO will it ever arrive here?
		} else {
			while (!StarPrinters.queueIsEmpty) {
				console.debug('Current receipts queue size:', StarPrinters.#printQueue.length)
				const [order, silent] = StarPrinters.#printQueue.shift()
				await StarPrinters.#reallyPrint(order, silent)
			}
		}
		StarPrinters.#isPrinting = false
	}

	/**
	 * @param order {Order}
	 * @param silent {boolean}
	 * @return {Promise<unknown>}
	 */
	static async #reallyPrint(order, silent) {
		console.debug(`Start printing order #${order.number}...`)
		StarPrinters.#isPrinting = true

		const printers = getEnabledPrinters(store.getState())
		if (!printers.length && !silent) {
			Alerts.warning(<StarPrinters.NoPrinterWarning/>, { perm: true })
			return
		}

		//all the Alerts.update() calls will just be no-op if the ID is 0, so we're safe without all the if's
		const maxProgress   = sumBy(printers, 'copies') + 2 //copies + receipt generation + file saving
		const progressAlert = !silent? await Alerts.info(i18next.t('Preparing receipt...'), { maxProgress, perm: true }) : 0
		// Alerts.startupProgress(progressAlert)

		const receiptResp = await order.getReceipt(silent)
		if (!receiptResp) {
			return //errors are already handled inside getReceipt()
		}
		Alerts.update(progressAlert, { content: i18next.t('The receipt is ready. Sending to {{count}} printer.', { count: printers.length }), progress: true })

		if (!platform.isNative() && !silent) {
			window.open(URL.createObjectURL(receiptResp.data)) //this is a developer test, so just open it in a new tab
		}

		return new Promise(resolve => {
			saveFile(`receipt-${order.id}.png`, receiptResp.data, file => {
				Alerts.progress(progressAlert) //no message, so we leave some split seconds to read the previous message
				let silenceEverything = !platform.isNative() && silent? '/silence' : ''//hack to silence mock prints
				const printInstructions = {
					uri       : silenceEverything + file.nativeURL,
					cutReceipt: true, //needed for iOS
					width     : 576, //TODO: this should be fed into the API and come from server constants
				}

				//since we do parallel print to each printer, we can't really update the alert text with the details
				const printSingleCopy = (printer, copy) => new Promise((resolve, reject) => {
					console.log(`Printing copy #${copy} of ${printer.copies} to ${printer.model} (${printer.port})`)
					window.starprnt.printImage(
						printer.port,
						printer.emulator,
						printInstructions,
						resolve,
						error => {
							const msg = StarPrinters.#rephraseTerriblyWrittenOrOverlyTechnicalStarPrinterErrors(error)
							reject({ msg, error, printer, copy })
						}
					)
				})

				const printResults = printers.map(async printer => {
					for (let copy = 1; copy <= printer.copies; copy++) { //poor man's printing queue
						await printSingleCopy(printer, copy)
							.finally(() => Alerts.progress(progressAlert))
					}
				})

				Promise.allSettled(printResults)
					.then(results => {
						Alerts.remove(progressAlert, true)
						console.log('All printing results', results)
						//region error alerts
						const errors = results.filter(e => e.status == 'rejected')
						if (errors.length) {
							const errorMsg = ({ reason }) => {
								if (reason instanceof Error) {
									return reason.message
								} else {
									//cannot inline this because the i18next scanner won't capture it........
									const copies = reason.printer.copies == 1? '' : i18next.t(', copy {{copy}} of {{copies}}', {
										copy   : reason.copy,
										copies : reason.printer.copies,
										context: 'Printing failed for printer XYZ (ABC, copy 1 of 3)',
									})
									return i18next.t('{{printer}} ({{port}}){{copies}} - {{reason}}', {
										printer: reason.printer.model,
										port   : reason.printer.port,
										reason : reason.msg,
										copies,
									})
								}
							}

							//the `silent` option is purposely ignored here - at some point the user MUST know (auto-)print failed
							console.error('Printing error(s)', errors.map(errorMsg))
							Alerts.danger((errors.length == 1?
								i18next.t('Printing failed for {{printerWithErrorMsg}}', { printerWithErrorMsg: errorMsg(errors[0]) })
								:
								<>
									<p>{i18next.t('Printing failed for:', { context: 'comes before a list of errors' })}</p>
									<ul>{errors.map((reason, i) => <li key={i}>{errorMsg(reason)}</li>)}</ul>
								</>
							), { perm: true })
						}
						//endregion
					})
					.finally(() => {
						file.remove()
						resolve()
					})
			})
		})
	}

	static #rephraseTerriblyWrittenOrOverlyTechnicalStarPrinterErrors(error) {
        //we can't rely on what comes after the colon, since sometimes there's nothing lol
        const msg = error.toLowerCase().replace(/[:.,/\d]*$/, '').trim()
        switch (msg) {
            case 'not connected': //unplugged USB printer
            case 'printer is power off': //unplugged Ethernet printer
            case 'fail to open port': //taken from the iOS source
            case 'open failed': //taken from USB code on decompiled StarIOPort3.1.jar
            case 'get system service failed': //taken from USB code on decompiled StarIOPort3.1.jar
            case 'device not found': //taken from USB code on decompiled StarIOPort3.1.jar
            case 'cannot connect to printer': //taken from decompiled StarIOPort3.1.jar
            case 'cannot find bluetooth printer': //taken from decompiled StarIOPort3.1.jar
            case 'read failed': //"..., socket might closed or timeout, read ret: -1" Bluetooth printer is off
            case 'unable to claim write': //connection issue with USB printer (removed cable right before printing)
            case 'read time out': //printer disconnected after sending print job but before promise's success
            case 'failed to connect to': //"... /IP (port XX) [...]" printer unplugged before the receipt got print
            case 'cannot find printer': //various sources, such as forgotten bluetooth pairing, unplugged USB in the middle of a job, and a couple of different files in the decompiled jar...
                return i18next.t('this printer seems turned off or disconnected.')

            case 'No bluetooth adapter found': //taken from decompiled StarIOPort3.1.jar
            case 'failed to write': //printer is configured but bluetooth got turned off after sending print job. However, this was also found on TCP and WTCP files from decompiled StarIOPort3.1.jar
                return i18next.t('cannot talk to printer; if this is a Bluetooth printer, check if it\'s enabled on this device.')

            case 'bluetooth adapter is off': //printer is configured but bluetooth got turned off before print job
                return i18next.t('Bluetooth seems to have been turned off.')

            case 'invalid bluetooth device name characters': //taken from decompiled StarIOPort3.1.jar
            case 'invalid bluetooth device name length': //taken from decompiled StarIOPort3.1.jar
            case 'invalid ios port name characters': //taken from decompiled StarIOPort3.1.jar
            case 'invalid ios port name length': //taken from decompiled StarIOPort3.1.jar
                return i18next.t('invalid Bluetooth device name; use up to 16 plain English characters.')

            case 'invalid pincode': //printer's error LED is blinking... out of paper?
            case 'invalid pincode characters length': //printer's error LED is blinking... out of paper?
                return i18next.t('incorrect pincode. It should be between 4 and 16 letters and/or numbers.')

            case 'printer is offline': //printer's error LED is blinking... out of paper?
            case 'Failed because printer is offline': //taken from decompiled StarIOPort3.1.jar
            case 'failed because printer is offline before print end counter is zero': //taken from decompiled StarIOPort3.1.jar
            case 'failed because printer is offline before print end counter is updated': //taken from decompiled StarIOPort3.1.jar
                return i18next.t('this printer refuses to print... is it out of paper?')

            //clear enough, but needs translation anyway
            case 'paper was not removed': //taken from decompiled StarIOPort3.1.jar
                return i18next.t('remove the paper before continuing.')

	        case 'empty paper': //paper roll ended in the middle of a job
	            console.log('PRINTER NEED PAPER 🧻') //thanks, Joel
                return i18next.t('this printer ran out of paper; you may need to print the order manually again, after refilling it.')

            case 'printer error':
            case 'write failed': //taken from USB code on decompiled StarIOPort3.1.jar
            case 'failed to read': //taken from decompiled StarIOPort3.1.jar
            case 'call-version': //taken from decompiled StarIOPort3.1.jar
            case 'Firmware check failed': //taken from decompiled StarIOPort3.1.jar
            case 'Firmware check firmware': //taken from decompiled StarIOPort3.1.jar
            case 'getport': //"...: call-version timeout" - taken from decompiled StarIOPort3.1.jar
            case 'tcp port': //"... \d is busy" - taken from decompiled StarIOPort3.1.jar
            case 'the port is null': //taken from decompiled StarIOPort3.1.jar
            case 'do not open a port': //taken from decompiled StarIOPort3.1.jar
            case 'failed to get parsed status': //taken from decompiled StarIOPort3.1.jar
            case 'failed to getstatus': //taken from decompiled StarIOPort3.1.jar
            case 'printer does not respond': //taken from decompiled StarIOPort3.1.jar
            case 'unable to read status': //taken from decompiled StarIOPort3.1.jar
            case 'failed to connect port within the timeout period': //taken from decompiled StarIOPort3.1.jar
            case 'returned the error response from printer': //taken from decompiled StarIOPort3.1.jar
            case 'returned write error response from printer': //taken from decompiled StarIOPort3.1.jar
            case 'could not read the response data': //taken from decompiled StarIOPort3.1.jar
            case 'called interrupt() during thread.sleep()': //taken from decompiled StarIOPort3.1.jar
            case 'Interup incomplete': //taken from decompiled StarIOPort3.1.jar
            case 'occured the timeout during reading the response data': //taken from decompiled StarIOPort3.1.jar
            case 'there was no response of the device within the timeout period': //taken from decompiled StarIOPort3.1.jar
            case 'there was no response of the printer within the timeout period': //taken from decompiled StarIOPort3.1.jar
                return i18next.t('cannot talk to printer; check if it\'s working before trying again.')

            case 'this device can not use this method': //"need to be supported BLE" taken from decompiled StarIOPort3.1.jar
            case 'this model is not supported this method': //taken from decompiled StarIOPort3.1.jar
            case 'unsupported function in this model': //sound related... taken from decompiled StarIOPort3.1.jar
            case 'failed to parse model and version': //taken from decompiled StarIOPort3.1.jar
            case 'failed to parse dip-switch1 condition': //taken from decompiled StarIOPort3.1.jar
            case 'the portable printer is not supported': //taken from decompiled StarIOPort3.1.jar
            case 'returned the non-supported response from printer': //taken from decompiled StarIOPort3.1.jar
            case 'returned write non-supported response from printer': //taken from decompiled StarIOPort3.1.jar
            case 'read unexpected response data': //taken from decompiled StarIOPort3.1.jar
            case 'This fuction is available form the bluetooth interface': //wat? taken from decompiled StarIOPort3.1.jar
            case 'Checked block is not available for this printer': //taken from decompiled StarIOPort3.1.jar
                return i18next.t('it seems this model is not yet fully supported. Contact support with the printer details for further help.')

            case 'no permission granted to access USB device': //unplugged Ethernet printer
            case 'permission denied': //taken from decompiled StarIOPort3.1.jar
                return i18next.t('no permission to talk to printer; try to print again and accept the permission dialog. If it doesn\'t show, check your device settings or contact support for help.')

            default:
                if (msg.match(/Not allowed to change below parameter/)) { //"... in this model («parameters»)" taken from decompiled StarIOPort3.1.jar
                    return i18next.t('it seems this model is not yet fully supported. Contact support with the printer details for further help.')
                }
                console.warn(`Raw printer error found: ${msg}`) //TODO Sentry
                return `${msg}.`
        }
	}

}
